import { convex } from "@/lib/convexClient";
import { UnifiedS3CredentialService } from "@/services/s3/UnifiedS3CredentialService";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { api } from "../../convex/_generated/api";

type PanoramaLike = {
  thumbnail_url?: string | null;
};

export interface S3DiagnosticResult {
  success: boolean;
  issues: string[];
  recommendations: string[];
  details: {
    craftspaceId: string;
    storageProvider: string;
    hasCredentials: boolean;
    clockOffset?: number;
    connectionTest?: boolean;
    signedUrlTest?: boolean;
  };
}

/**
 * Comprehensive S3 diagnostics to identify common issues
 */
export const runS3Diagnostics = async (craftspaceId: string): Promise<S3DiagnosticResult> => {
  const result: S3DiagnosticResult = {
    success: true,
    issues: [],
    recommendations: [],
    details: {
      craftspaceId,
      storageProvider: "unknown",
      hasCredentials: false,
    },
  };

  try {
    // 1. Check craftspace storage provider
    const storageProvider = await StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId);
    result.details.storageProvider = storageProvider || "unknown";

    if (storageProvider !== "custom-s3") {
      result.issues.push(`Craftspace is using ${storageProvider} storage, not custom S3`);
      return result;
    }

    // 2. Check if credentials exist
    const hasCredentials = await UnifiedS3CredentialService.hasValidCredentials(craftspaceId);
    result.details.hasCredentials = hasCredentials;

    if (!hasCredentials) {
      result.issues.push("No S3 credentials found for craftspace");
      result.recommendations.push("Configure S3 credentials in craftspace settings");
      result.success = false;
      return result;
    }

    // 3. Test storage service creation
    let storageService: unknown;
    try {
      storageService = await StorageServiceFactory.createStorageService(craftspaceId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      result.issues.push(`Failed to create storage service: ${message}`);
      result.success = false;

      if (message.includes("No S3 credentials found")) {
        result.recommendations.push("Reconfigure S3 credentials in craftspace settings");
      } else if (message.includes("InvalidAccessKeyId")) {
        result.recommendations.push("Verify S3 Access Key ID is correct");
      } else if (message.includes("SignatureDoesNotMatch")) {
        result.recommendations.push("Check S3 Secret Access Key and system clock synchronization");
      }

      return result;
    }

    // 4. Test basic connection
    try {
      const connectionResult = await storageService.testConnection();
      result.details.connectionTest = connectionResult.success;

      if (!connectionResult.success) {
        result.issues.push(`S3 connection test failed: ${connectionResult.error}`);
        result.recommendations.push("Check S3 endpoint URL and network connectivity");
        result.success = false;
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      result.issues.push(`Connection test error: ${message}`);
      result.success = false;
    }

    // 5. Test signed URL generation with a dummy path
    try {
      const testPath = "test/diagnostic-file.jpg";
      const signedUrl = await storageService.getFileUrl(testPath);
      result.details.signedUrlTest = !!signedUrl;

      if (!signedUrl) {
        result.issues.push("Failed to generate signed URL");
        result.success = false;
      } else {
        // Test if signed URL is accessible (should return 404 but not 403)
        try {
          const response = await fetch(signedUrl, { method: "HEAD" });
          if (response.status === 403) {
            result.issues.push("Generated signed URLs return 403 Forbidden");
            result.recommendations.push("Check S3 bucket policies and IAM permissions");
            result.success = false;
          }
        } catch (_fetchError) {
          // Network errors are expected for test URLs
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      result.issues.push(`Signed URL generation failed: ${message}`);
      result.success = false;
    }

    // 6. Check system clock (approximate)
    const clockOffset = await checkClockOffset();
    result.details.clockOffset = clockOffset;

    if (Math.abs(clockOffset) > 300000) {
      // More than 5 minutes
      result.issues.push(
        `System clock appears to be off by ${Math.round(clockOffset / 1000)} seconds`,
      );
      result.recommendations.push("Synchronize system clock (S3 signatures are time-sensitive)");
      result.success = false;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown error";
    result.issues.push(`Diagnostic error: ${message}`);
    result.success = false;
  }

  return result;
};

/**
 * Estimate clock offset by comparing with a time server
 */
const checkClockOffset = async (): Promise<number> => {
  try {
    const start = Date.now();
    const response = await fetch("https://worldtimeapi.org/api/timezone/UTC", {
      method: "HEAD",
    });
    const networkDelay = (Date.now() - start) / 2;

    const serverTime = new Date(response.headers.get("Date") || "").getTime();
    const localTime = Date.now() - networkDelay;

    return localTime - serverTime;
  } catch {
    // If we can't reach time server, assume clock is fine
    return 0;
  }
};

/**
 * Global function for manual S3 testing (can be called from browser console)
 */
const globalWindow = window as Window & {
  testS3Connection?: (craftspaceId?: string) => Promise<S3DiagnosticResult | undefined>;
};
globalWindow.testS3Connection = async (craftspaceId?: string) => {
  try {
    if (!craftspaceId) {
      // Try to get craftspace from current URL
      const urlParts = window.location.pathname.split("/");
      const projectIndex = urlParts.indexOf("project");
      if (projectIndex !== -1 && urlParts[projectIndex + 1]) {
        const projectId = urlParts[projectIndex + 1];
        const status = await checkProjectS3Status(projectId);
        craftspaceId = status.craftspaceId;
      }
    }

    if (!craftspaceId || craftspaceId === "default") {
      console.log("ℹ️ This project uses default 360Crafter storage, not custom S3");
      return;
    }

    console.log("🔍 Running S3 diagnostics for craftspace:", craftspaceId);
    const result = await runS3Diagnostics(craftspaceId);

    console.group("📊 S3 Diagnostic Results");
    console.log("Success:", result.success ? "✅" : "❌");
    console.log("Details:", result.details);

    if (result.issues.length > 0) {
      console.group("🚨 Issues Found:");
      // biome-ignore lint/suspicious/useIterableCallbackReturn: side effects
      result.issues.forEach((issue) => console.error(`  • ${issue}`));
      console.groupEnd();
    }

    if (result.recommendations.length > 0) {
      console.group("💡 Recommendations:");
      // biome-ignore lint/suspicious/useIterableCallbackReturn: side effects
      result.recommendations.forEach((rec) => console.log(`  • ${rec}`));
      console.groupEnd();
    }

    console.groupEnd();
    return result;
  } catch (error) {
    console.error("❌ Diagnostic test failed:", error);
  }
};

/**
 * Quick function to check if S3 URLs are working for a specific project
 */
export const checkProjectS3Status = async (
  projectId: string,
): Promise<{
  craftspaceId: string;
  totalPanoramas: number;
  workingUrls: number;
  failingUrls: number;
  diagnostics?: S3DiagnosticResult;
}> => {
  try {
    const panoramas = (await convex.query(api.panoramas.listByProject, {
      projectId,
    })) as PanoramaLike[] | null;

    if (!panoramas || panoramas.length === 0) {
      return {
        craftspaceId: "unknown",
        totalPanoramas: 0,
        workingUrls: 0,
        failingUrls: 0,
      };
    }

    let craftspaceId = "default";
    try {
      const project = await convex.query(api.projects.get, { id: projectId });
      craftspaceId = project?.craftspace_id || "default";
    } catch {
      craftspaceId = "default";
    }
    let workingUrls = 0;
    let failingUrls = 0;

    // Test a few thumbnail URLs quickly
    const urlsToTest = panoramas
      .filter((p) => p.thumbnail_url)
      .slice(0, 5) // Test max 5 URLs to avoid overwhelming
      .map((p) => p.thumbnail_url)
      .filter((url): url is string => !!url);

    for (const url of urlsToTest) {
      try {
        const response = await fetch(url, {
          method: "HEAD",
          signal: AbortSignal.timeout(5000),
        });

        if (response.ok) {
          workingUrls++;
        } else {
          failingUrls++;
        }
      } catch {
        failingUrls++;
      }
    }

    let diagnostics: S3DiagnosticResult | undefined;
    if (failingUrls > 0 && craftspaceId !== "default") {
      diagnostics = await runS3Diagnostics(craftspaceId);
    }

    return {
      craftspaceId,
      totalPanoramas: panoramas.length,
      workingUrls,
      failingUrls,
      diagnostics,
    };
  } catch (error) {
    console.error("Error checking project S3 status:", error);
    return {
      craftspaceId: "unknown",
      totalPanoramas: 0,
      workingUrls: 0,
      failingUrls: 0,
    };
  }
};
